import React from 'react';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import ShopThumb, {
  Props as ShopThumbProps,
  ShopThumbType,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Shop';
import IconWithDropdown from '@wix/da-shared-react/pkg/Dropdown/Preset/IconWithDropdown';
import EllipsisIcon from '@wix/da-shared-react/pkg/Icons/Ellipsis';
import LockIcon from '@wix/da-shared-react/pkg/Icons/Lock';
import UnlockIcon from '@wix/da-shared-react/pkg/Icons/Unlock';
import PencilIcon from '@wix/da-shared-react/pkg/Icons/Pencil';
import TrashcanIcon from '@wix/da-shared-react/pkg/Icons/Trashcan';
import {
  default as CommissionConfirmation,
  Props as CommissionConfirmationProps,
} from '@wix/da-shared-react/pkg/CommissionEditPopup/CommissionConfirmation';
import {
  EditDeviationPayload,
  DeleteDeviationPayload,
} from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { PapiDeviation } from '@wix/da-types-papi';

import s from './CommissionThumbWithMenu.scss';

export interface ActionProps {
  className?: string;
  isOwner: boolean;
  isActive?: boolean;
  isDeleted?: boolean;
  editDeviation: (object: EditDeviationPayload) => void;
  toggleCommissionStatus: (object: PapiDeviation) => void;
  deleteDeviation: (object: DeleteDeviationPayload) => void;
}

export interface State {
  confirmationPopupType?: CommissionConfirmationProps['confirmationType'];
}

export class CommissionThumbWithMenu extends React.Component<
  ShopThumbProps & ActionProps & WithTranslation,
  State
> {
  readonly state: State = {
    confirmationPopupType: undefined,
  };

  openCommissionPauseConfirmation = event => {
    event.preventDefault();
    const { isActive, isDeleted } = this.props;
    if (isDeleted) {
      return;
    }

    this.setState({
      confirmationPopupType: isActive ? 'disable' : 'enable',
    });
  };

  onChangeCommissionStatus = () => {
    const { deviation } = this.props;

    if (!deviation.commission) {
      return;
    }
    this.props.toggleCommissionStatus(deviation);
    this.onCloseConfirmationModal();
  };

  openCommissionDeleteConfirmation = event => {
    event.preventDefault();

    this.setState({
      confirmationPopupType: 'remove',
    });
  };

  onDeleteCommission = () => {
    this.props.deleteDeviation({ deviation: this.props.deviation });
    this.setState({
      confirmationPopupType: undefined,
    });
  };

  onCloseConfirmationModal = () => {
    this.setState({
      confirmationPopupType: undefined,
    });
  };

  openCommissionEditModal = event => {
    const { editDeviation, deviation } = this.props;
    if (!deviation.isTextEditable) {
      return;
    }
    event.preventDefault();

    editDeviation({ deviation });
  };

  render() {
    const { t, isOwner, deviation, className, isActive } = this.props;
    const { confirmationPopupType } = this.state;

    return (
      <div className={classnames(s['root'], className)}>
        <ShopThumb
          {...this.props}
          showAuthor={false}
          type={ShopThumbType.COMMISSION}
        />
        {isOwner && (
          <div className={s['dropdown-wrapper']}>
            <IconWithDropdown
              usePortal
              className={s['dropdown']}
              icon={<EllipsisIcon withPadding />}
              menuAlignment="right"
              items={[
                {
                  // safe zone
                  items: [
                    {
                      icon: <PencilIcon />,
                      label: t!('common.edit'),
                      onClick: this.openCommissionEditModal,
                      componentClass: 'span',
                      componentProps: !deviation.isTextEditable
                        ? {
                            title: t!('common.commissions.legacyNotEditable'),
                            style: {
                              pointerEvents: 'all',
                              cursor: 'default',
                              background: 'none',
                            },
                          }
                        : {},
                      disabled: !deviation.isTextEditable,
                    },
                    {
                      icon: isActive ? <UnlockIcon /> : <LockIcon />,
                      label: isActive ? t!('common.close') : t!('common.open'),
                      onClick: this.openCommissionPauseConfirmation,
                    },
                  ],
                },
                {
                  // danger zone
                  items: [
                    {
                      icon: <TrashcanIcon />,
                      label: t!(`common.delete`),
                      onClick: this.openCommissionDeleteConfirmation,
                      danger: true,
                    },
                  ],
                },
              ]}
            />
          </div>
        )}
        {confirmationPopupType && (
          <CommissionConfirmation
            isOpen
            confirmationType={confirmationPopupType}
            onCancel={this.onCloseConfirmationModal}
            onConfirm={
              confirmationPopupType === 'remove'
                ? this.onDeleteCommission
                : this.onChangeCommissionStatus
            }
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(CommissionThumbWithMenu);
